import { Matrix4, Quaternion, type SpriteMaterial } from 'three';
import type { Vector2 } from 'three';
import { markRaw, type Raw } from 'vue';
import { spriteNode, createLabelSpriteMaterial, type SpriteNode } from '@/lib/threeScene/nodes/sprite';
import { vector3 } from '@/domain/geometry/vector3';
import { formatDegrees } from '@/domain/geometry/formatMath';
import type { LabelPlacement } from '@/views/3d-planner/hipSpine/labelOverlapResolver';

export type AngleLabelPlacement = LabelPlacement & {
    angleId: string;
    groupId: string;
    material: Raw<SpriteMaterial>;
};

export type PendingAngleLabel = AngleLabelPlacement & {
    groupIndex: number;
};

export type LabelPlacementInput = {
    angleId: string;
    groupId: string;
    position: Vector2;
    value: number;
    colour: string;
    sphereRadius: number;
};

export function prepareLabelPlacement(input: LabelPlacementInput): AngleLabelPlacement | null {
    const labelText = formatDegrees(input.value, { precision: 1 });
    const labelResult = createLabelSpriteMaterial(labelText, input.colour);
    if (!labelResult) return null;
    const labelHeight = input.sphereRadius * 5;
    const labelWidth = labelHeight * labelResult.aspectRatio;
    return {
        angleId: input.angleId,
        groupId: input.groupId,
        position: input.position.clone(),
        halfWidth: labelWidth / 2,
        halfHeight: labelHeight / 2,
        material: markRaw(labelResult.material),
    };
}

export function buildLabelFromPlacement(pending: PendingAngleLabel): SpriteNode {
    return spriteNode(`label-${pending.angleId}`, {
        parent: pending.groupId,
        material: pending.material,
        transform: new Matrix4().compose(
            vector3(pending.position.x, pending.position.y, 0),
            new Quaternion(),
            vector3(pending.halfWidth * 2, pending.halfHeight * 2, 1),
        ),
    });
}
